'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { interpolateStringWithData } from 'utils';
import { useIsClient } from '../../../lib/hooks/useIsClient';
import { c } from '../../../utils/tailwind-merge';
import { Alert } from '../../atoms/Alert/Alert';
import { Separator } from '../../atoms/Separator/Separator';
import { Gallery } from '../../molecules/Gallery/Gallery';
import { ProductBreadcrumbs } from '../../molecules/ProductBreadcrumbs/ProductBreadcrumbs';
import { EntitlementConfirmation } from '../../organisms/EntitlementConfirmation/EntitlementConfirmation';
import { ProductInfo } from '../../organisms/ProductInfo/ProductInfo';
import { ProductNutritionLabels } from '../../organisms/ProductNutritionLabels/ProductNutritionLabels';
import { ProductTabs } from '../../organisms/ProductTabs/ProductTabs';
import { ProductTabValue } from '../../organisms/ProductTabs/ProductTabs.types';
import { RelatedKeywords } from '../../organisms/RelatedKeywords/RelatedKeywords';
import { UpmProductDetailTemplateProps } from './UpmProductDetailTemplate.types';

export const UpmProductDetailTemplate = ({
  breadcrumbs,
  currentTab,
  entitlementConfirmation,
  ownedDate,
  gallery,
  productInfo,
  nutritionLabels,
  tabs,
  relatedKeywords,
  wasEntitled,
  microcopy,
  className = '',
  previewMode = false,
  onClickChipList,
  onClickProductInfo,
  onOpenInUnityFromEntitlement,
  onGoToMyAssets,
  onSeeOwnedPackages,
  onOpenInUnityFromPackage,
  onCloseFromEntitlement,
  onHandleTabChange,
  onOpenCookieSettings,
  onAccordionChange,
}: UpmProductDetailTemplateProps) => {
  const isClient = useIsClient();
  const [activeTab, setActiveTab] = useState<ProductTabValue>(currentTab);

  const [isProductInfoSticky, setIsProductInfoSticky] = useState(false);
  const prevStickyRef = useRef(false);

  const desktopObserverRef = useRef<IntersectionObserver | null>(null);
  const mobileObserverRef = useRef<IntersectionObserver | null>(null);
  const [hideSticky, setHideSticky] = useState(false);

  const makeObserver = useCallback(
    (el: HTMLElement, threshold: number) => {
      if (!isClient) return null;

      const cb: IntersectionObserverCallback = (entries) => {
        const entry = entries[0];

        if (!el || el.offsetParent === null) return;

        const nextSticky = !entry.isIntersecting;

        if (nextSticky !== prevStickyRef.current) {
          prevStickyRef.current = nextSticky;
          setIsProductInfoSticky(nextSticky);
        }
      };

      const obs = new IntersectionObserver(cb, {
        root: null,
        threshold,
      });

      obs.observe(el);
      return obs;
    },
    [isClient]
  );

  useEffect(() => {
    if (!isClient) return;
    const footerEl = document.getElementById('layoutFooter');
    if (!footerEl) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setHideSticky(entry.isIntersecting);
      },
      { root: null, threshold: 0.125 }
    );
    observer.observe(footerEl);
    return () => observer.disconnect();
  }, [isClient, setHideSticky]);

  const onDesktopCtaMount = useCallback(
    (el: HTMLElement | null) => {
      if (desktopObserverRef.current) {
        desktopObserverRef.current.disconnect();
        desktopObserverRef.current = null;
      }
      if (el && isClient) {
        const observer = makeObserver(el, 0);
        if (observer) {
          desktopObserverRef.current = observer;
        }
      }
    },
    [makeObserver, isClient]
  );

  const onMobileCtaMount = useCallback(
    (el: HTMLElement | null) => {
      if (mobileObserverRef.current) {
        mobileObserverRef.current.disconnect();
        mobileObserverRef.current = null;
      }
      if (el && isClient) {
        const observer = makeObserver(el, 0.2);
        if (observer) {
          mobileObserverRef.current = observer;
        }
      }
    },
    [makeObserver, isClient]
  );

  useEffect(() => {
    return () => {
      desktopObserverRef.current?.disconnect();
      mobileObserverRef.current?.disconnect();
    };
  }, []);

  const handleTabChange = useCallback(
    (tab: ProductTabValue) => {
      setActiveTab(tab);
      onHandleTabChange(tab);
    },
    [onHandleTabChange]
  );

  useEffect(() => {
    setActiveTab(currentTab);
  }, [currentTab]);

  const renderPurchasedItemAlert = () => {
    return ownedDate ? (
      <Alert
        label={interpolateStringWithData(microcopy.ownedDate, {
          date: ownedDate,
        })}
        className="my-4"
      />
    ) : null;
  };

  const renderDesktop = () => (
    <section className="hidden lg:block">
      {wasEntitled && (
        <EntitlementConfirmation
          {...entitlementConfirmation}
          microcopy={microcopy.entitlementConfirmation}
          onGoToMyAssets={onGoToMyAssets}
          onOpenInUnityFromEntitlement={onOpenInUnityFromEntitlement}
          onSeeOwnedPackages={onSeeOwnedPackages}
          onCloseFromEntitlement={onCloseFromEntitlement}
        />
      )}

      {isProductInfoSticky && (
        <ProductInfo
          {...productInfo}
          variant="sticky"
          microcopy={microcopy.productInfo}
          onClickProductInfo={previewMode ? undefined : onClickProductInfo}
        />
      )}

      <div className="container w-full">
        {renderPurchasedItemAlert()}
        <ProductBreadcrumbs {...breadcrumbs} className="mb-1" />

        <div className="grid grid-cols-[minmax(0,_1fr)_396px] items-start gap-6">
          <div className="flex min-w-0 flex-col gap-6 self-start">
            <Gallery
              {...gallery}
              microcopy={microcopy.gallery}
              position="details"
              aspectRatio="aspect-[3/2]"
              onOpenCookieSettings={onOpenCookieSettings}
            />
            <ProductTabs
              onFilterReviews={() => {}}
              onWriteReview={() => {}}
              onHelpfulVotesReview={() => {}}
              onEditReview={() => {}}
              onDeleteReview={() => {}}
              onHelpfulVotesReply={() => {}}
              onEditReply={() => {}}
              onDeleteReply={() => {}}
              onSubmitReply={() => {}}
              onFilter={() => {}}
              onSort={() => {}}
              onPageChange={() => {}}
              onSubmitReport={() => {}}
              {...tabs}
              microcopy={microcopy.tabs}
              value={activeTab}
              onValueChange={handleTabChange}
              onOpenInUnityFromPackage={
                previewMode ? undefined : onOpenInUnityFromPackage
              }
              onAccordionChange={onAccordionChange}
            />
            <Separator className="mt-16" />
            <RelatedKeywords
              {...relatedKeywords}
              microcopy={microcopy.relatedKeywords}
              onClickChipList={onClickChipList}
            />
          </div>

          <aside className="flex flex-col gap-6 lg:shrink-0">
            <div
              aria-hidden={isProductInfoSticky}
              className={
                isProductInfoSticky
                  ? 'pointer-events-none select-none px-4 opacity-0'
                  : ''
              }
            >
              <ProductInfo
                {...productInfo}
                variant="default"
                microcopy={microcopy.productInfo}
                onClickProductInfo={
                  previewMode ? undefined : onClickProductInfo
                }
                onCallToActionMount={onDesktopCtaMount}
              />
            </div>

            <ProductNutritionLabels
              {...nutritionLabels}
              microcopy={microcopy.nutritionLabels}
            />
          </aside>
        </div>
      </div>
    </section>
  );

  const renderMobile = () => (
    <section
      className={c(
        'grid grid-cols-1 gap-6 lg:hidden',
        isProductInfoSticky && 'pb-20'
      )}
    >
      {isProductInfoSticky && !hideSticky && (
        <ProductInfo
          {...productInfo}
          variant="sticky"
          microcopy={microcopy.productInfo}
          onClickProductInfo={previewMode ? undefined : onClickProductInfo}
        />
      )}

      {renderPurchasedItemAlert()}
      <ProductBreadcrumbs {...breadcrumbs} className="mb-0 px-4" />

      <div
        aria-hidden={isProductInfoSticky}
        className={
          isProductInfoSticky
            ? 'pointer-events-none select-none opacity-5'
            : 'p-5'
        }
      >
        <ProductInfo
          {...productInfo}
          variant="default"
          microcopy={microcopy.productInfo}
          onClickProductInfo={previewMode ? undefined : onClickProductInfo}
          onCallToActionMount={onMobileCtaMount}
        />
      </div>

      <Gallery
        {...gallery}
        position="details"
        microcopy={microcopy.gallery}
        aspectRatio="aspect-[3/2]"
        onOpenCookieSettings={onOpenCookieSettings}
      />
      <ProductTabs
        onFilterReviews={() => {}}
        onWriteReview={() => {}}
        onHelpfulVotesReview={() => {}}
        onEditReview={() => {}}
        onDeleteReview={() => {}}
        onHelpfulVotesReply={() => {}}
        onEditReply={() => {}}
        onDeleteReply={() => {}}
        onSubmitReply={() => {}}
        onFilter={() => {}}
        onSort={() => {}}
        onPageChange={() => {}}
        onSubmitReport={() => {}}
        {...tabs}
        microcopy={microcopy.tabs}
        value={activeTab}
        onValueChange={onHandleTabChange}
        onOpenInUnityFromPackage={
          previewMode ? undefined : onOpenInUnityFromPackage
        }
        onAccordionChange={onAccordionChange}
        className="px-4"
      />
      <div>
        <ProductNutritionLabels
          {...nutritionLabels}
          microcopy={microcopy.nutritionLabels}
          className="px-4"
        />
        <Separator />
        <RelatedKeywords
          {...relatedKeywords}
          microcopy={microcopy.relatedKeywords}
          className="px-4"
          onClickChipList={onClickChipList}
        />
      </div>
    </section>
  );

  return (
    <div className={className}>
      {renderDesktop()}
      {renderMobile()}
    </div>
  );
};
